import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { Badge } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { formatDateTime } from '@/lib/datetime'
import { formatMoney } from '@/lib/money'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import { AtDoorSettlement } from './AtDoorSettlement'
import { useAdminContext } from '../session'
import type {
  DrinkOption,
  ReservationDetail,
  ReservationTableDetail,
  TableTypeChoice,
} from '../types'

/**
 * Une réservation ouverte : exactement ce qu'elle contient.
 *
 * Acheteur et contact, chaque convive, la table (type, formule, nom), ce
 * qu'elle comprend en bouteilles — sa propre composition, pas la formule
 * d'aujourd'hui —, l'argent, et où elle en est ce soir : table attribuée,
 * arrivée, installée par qui. Une réservation de billets seuls n'a ni table ni
 * bouteilles : ces blocs n'apparaissent pas.
 *
 * La composition d'une table se modifie ici, pour elle seule. Le prix ne
 * bouge pas — l'écran le dit avant qu'on valide —, et le motif est demandé :
 * il part au journal avec l'avant et l'après.
 */
export function ReservationDetailPanel({
  orderId,
  onClose,
}: {
  orderId: string
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const { eventId, event } = useAdminContext()
  const timeZone = event?.timezone ?? 'UTC'
  const ref = useRef<HTMLElement>(null)
  const query = useAdminQuery<ReservationDetail>(
    ['admin-reservation', eventId, orderId],
    `/admin/events/${eventId}/reservations/${orderId}`,
    eventId,
  )
  const detail = query.data

  // Ouvrir une réservation plus bas dans la liste ramène le détail sous les
  // yeux : sur téléphone, il serait sinon trois écrans plus haut.
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [orderId])

  // Nul : montant retiré par le serveur (la porte). Les blocs d'argent ne
  // s'affichent alors pas ; le tiret n'est qu'un filet.
  const money = (minor: number | null | undefined) =>
    minor === null || minor === undefined
      ? '—'
      : formatMoney({ amount_minor: minor, currency: detail?.currency ?? 'EUR' }, i18n.language)
  const amountsVisible = detail?.amounts_visible !== false
  const when = (iso: string) => formatDateTime(iso, i18n.language, timeZone)

  return (
    <section
      ref={ref}
      aria-label={t('admin.reservations.detail.title')}
      className="mb-6 min-w-0 scroll-mt-4 rounded-lg border border-champagne-400/40 bg-bg-elevated p-4 sm:p-6"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.reservations.detail.title')}
          </p>
          <h2 className="mt-1 break-words font-display text-heading-md text-content-primary">
            {detail
              ? detail.anonymized
                ? t('admin.reservations.anonymized')
                : [detail.first_name, detail.last_name].filter(Boolean).join(' ') ||
                  t('admin.reservations.noName')
              : '…'}
          </h2>
          {detail ? (
            <p className="mt-0.5 text-caption text-content-muted">
              {detail.order_number} · {when(detail.created_at)}
            </p>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t('admin.reservations.detail.close')}
        </Button>
      </div>

      {query.isError ? (
        <p role="alert" className="mt-4 text-sm text-state-danger">
          {t('admin.reservations.loadFailed')}
        </p>
      ) : !detail ? (
        <p className="mt-4 text-sm text-content-muted">{t('admin.reservations.loading')}</p>
      ) : (
        <div className="mt-4 grid gap-6 lg:grid-cols-2">
          <div className="min-w-0 space-y-5">
            <Block title={t('admin.reservations.detail.contact')}>
              {detail.email || detail.whatsapp || detail.phone ? (
                <ul className="space-y-0.5 text-sm">
                  {detail.email ? (
                    <li>
                      <a href={`mailto:${detail.email}`} className="break-all text-champagne-300 underline">
                        {detail.email}
                      </a>
                    </li>
                  ) : null}
                  {detail.whatsapp ? (
                    <li className="break-all text-content-secondary">WhatsApp · {detail.whatsapp}</li>
                  ) : null}
                  {detail.phone && detail.phone !== detail.whatsapp ? (
                    <li className="break-all text-content-secondary">{detail.phone}</li>
                  ) : null}
                </ul>
              ) : (
                <p className="text-sm text-content-muted">{t('admin.reservations.noContact')}</p>
              )}
            </Block>

            <Block
              title={t('admin.reservations.detail.participants', { count: detail.people })}
            >
              {detail.participants.length > 0 ? (
                <ul className="space-y-0.5 text-sm text-content-primary">
                  {detail.participants.map((person, index) => (
                    <li key={`${person.name}-${index}`} className="flex flex-wrap gap-x-2">
                      <span className="break-words">{person.name}</span>
                      {person.primary ? (
                        <span className="text-caption text-content-muted">
                          {t('admin.reservations.detail.buyer')}
                        </span>
                      ) : null}
                      {person.checked_in_at ? (
                        <span className="text-caption text-state-success">
                          {t('admin.reservations.detail.checkedIn')}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-content-muted">
                  {t('admin.reservations.detail.noParticipants')}
                </p>
              )}
              {detail.bracelets ? (
                <p className="mt-2 text-sm text-content-secondary">
                  {t('admin.orders.bracelets.badge', { count: detail.bracelets })}
                </p>
              ) : null}
              <p className="mt-2 text-caption text-content-muted">
                {detail.admission
                  ? t('admin.reservations.detail.admissionYes')
                  : t('admin.reservations.detail.admissionNo')}
                {detail.linked_order_number
                  ? ` · ${t('admin.reservations.detail.linked', { order: detail.linked_order_number })}`
                  : ''}
              </p>
            </Block>

            <Block title={t('admin.reservations.detail.lines')}>
              <ul className="divide-y divide-line-subtle text-sm">
                {detail.lines.map((line, index) => (
                  <li key={index} className="flex items-baseline justify-between gap-3 py-1.5">
                    <span className="min-w-0 break-words text-content-primary">
                      {line.quantity} × {line.label}
                      {line.detail ? (
                        <span className="block text-caption text-content-muted">{line.detail}</span>
                      ) : null}
                    </span>
                    {amountsVisible ? (
                      <span className="shrink-0 tabular-nums text-content-secondary">
                        {money(line.line_total_minor)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              <dl className="mt-2 space-y-0.5 text-sm">
                {amountsVisible ? (
                  <>
                    {(detail.discount_minor ?? 0) > 0 ? (
                      <Row
                        label={t('admin.orders.discount')}
                        value={`− ${money(detail.discount_minor)}`}
                      />
                    ) : null}
                    <Row label={t('admin.orders.total')} value={money(detail.total_minor)} strong />
                    <Row label={t('admin.orders.paid')} value={money(detail.paid_minor)} />
                    {(detail.refunded_minor ?? 0) > 0 ? (
                      <Row
                        label={t('admin.orders.refunded')}
                        value={money(detail.refunded_minor)}
                      />
                    ) : null}
                  </>
                ) : null}
                <Row
                  label={t('admin.reservations.col.payment')}
                  value={
                    detail.payment === 'closed'
                      ? t(`admin.orders.status.${detail.status}`)
                      : t(`admin.reservations.payment.${detail.payment}`)
                  }
                />
              </dl>
              {!amountsVisible ? (
                <p className="mt-1 text-caption text-content-muted">
                  {t('admin.orders.amountsHidden')}
                </p>
              ) : null}
              <AtDoorSettlement
                pending={detail.status === 'pending_payment'}
                paysAtDoor={Boolean(detail.pays_at_door)}
                dueMinor={detail.amount_due_minor}
                settledAtDoor={Boolean(detail.settled_at_door)}
                method={detail.settled_method}
                by={detail.settled_by}
                paidAt={detail.paid_at}
                money={money}
              />
            </Block>
          </div>

          <div className="min-w-0 space-y-5">
            {detail.needs_table_type ? (
              <p
                role="alert"
                className="rounded-md border border-state-warning/40 bg-state-warning/5 px-3 py-2 text-sm text-state-warning"
              >
                {t('admin.reservations.detail.needsTableType')}{' '}
                <Link
                  to={`../commandes?q=${encodeURIComponent(detail.order_number)}`}
                  relative="path"
                  className="underline"
                >
                  {t('admin.reservations.detail.openOrder')}
                </Link>
              </p>
            ) : null}
            {detail.tables.length === 0 ? (
              <p className="text-sm text-content-muted">{t('admin.reservations.detail.noTable')}</p>
            ) : (
              detail.tables.map((table) => (
                <TableBlock
                  key={table.reservation_id}
                  table={table}
                  orderId={orderId}
                  money={money}
                  when={when}
                />
              ))
            )}
            {detail.table_request_id ? (
              <Link
                to={`../demandes-tables`}
                relative="path"
                className="inline-block text-sm text-champagne-300 underline"
              >
                {t('admin.reservations.detail.request')}
              </Link>
            ) : null}
          </div>
        </div>
      )}
    </section>
  )
}

function TableBlock({
  table,
  orderId,
  money,
  when,
}: {
  table: ReservationTableDetail
  orderId: string
  money: (minor: number | null | undefined) => string
  when: (iso: string) => string
}) {
  const { t } = useTranslation()
  const { can } = useAdminContext()
  const [editing, setEditing] = useState<'composition' | 'type' | null>(null)
  const cancelled = table.status === 'cancelled'

  const physical = [table.physical_table_number, table.physical_table_name]
    .filter(Boolean)
    .join(' · ')

  return (
    <Block
      title={
        table.custom
          ? t('admin.reservations.detail.customTable')
          : t('admin.reservations.detail.table')
      }
    >
      {/* Une table personnalisée : sa composition est celle validée pour ce
          client, et la table d'origine reste nommée pour s'y retrouver. */}
      {table.customized ? (
        <p className="mb-1 flex flex-wrap items-center gap-2">
          <Badge tone="champagne">{t('admin.reservations.detail.customized')}</Badge>
          <span className="text-caption text-content-muted">
            {t('admin.reservations.detail.customizedFrom')}
          </span>
        </p>
      ) : null}
      <p className="break-words text-content-primary">
        {table.table_type_label}
        {table.package_label ? ` · ${table.package_label}` : ''}
      </p>
      {table.name ? (
        <p className="break-words text-sm text-champagne-300">{table.name}</p>
      ) : null}
      <p className="text-sm text-content-secondary">
        {t('admin.reservations.peopleCount', { count: table.party_size })} ·{' '}
        {table.admission_included
          ? t('admin.reservations.detail.admissionYes')
          : t('admin.reservations.detail.admissionNo')}
      </p>

      <p className="mt-3 text-caption uppercase tracking-wide text-content-muted">
        {t('admin.reservations.detail.bottles')}
      </p>
      {table.items.length > 0 ? (
        <ul className="mt-1 space-y-0.5 text-sm">
          {table.items.map((item) => (
            <li key={item.drink_item_id} className="flex flex-wrap justify-between gap-x-3">
              <span className="min-w-0 break-words text-content-primary">
                {item.quantity} × {item.label}
              </span>
              <span className="text-caption text-content-muted">
                {item.included_quantity >= item.quantity
                  ? t('admin.reservations.detail.included')
                  : item.line_total_minor === null
                    ? t('admin.reservations.detail.partlyIncludedNoAmount', {
                        count: item.included_quantity,
                      })
                    : t('admin.reservations.detail.partlyIncluded', {
                        count: item.included_quantity,
                        amount: money(item.line_total_minor),
                      })}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-sm text-content-muted">{t('admin.reservations.detail.noBottles')}</p>
      )}
      {table.composition_changed_at ? (
        <p className="mt-1 text-caption text-champagne-300">
          {t('admin.reservations.detail.changed', {
            date: when(table.composition_changed_at),
            who: table.composition_changed_by ?? '—',
          })}
        </p>
      ) : null}

      <p className="mt-3 text-caption uppercase tracking-wide text-content-muted">
        {t('admin.reservations.detail.placement')}
      </p>
      <ul className="mt-1 space-y-0.5 text-sm text-content-secondary">
        <li>
          {physical
            ? t('admin.reservations.detail.assigned', {
                table: physical,
                zone: table.physical_table_zone ? ` (${table.physical_table_zone})` : '',
              })
            : t('admin.reservations.detail.notPlaced')}
        </li>
        <li>
          {table.arrived_at
            ? t('admin.reservations.detail.arrived', { date: when(table.arrived_at) })
            : t('admin.reservations.detail.notArrived')}
        </li>
        {table.seated_at ? (
          <li>
            {t('admin.reservations.detail.seated', {
              date: when(table.seated_at),
              who: table.seated_by ?? '—',
            })}
          </li>
        ) : null}
        {table.balance_due ? (
          <li className="text-caption text-state-warning">
            {t('admin.reservations.detail.balanceDue')}
          </li>
        ) : null}
        {!table.placeable && !cancelled ? (
          <li className="text-caption text-content-muted">
            {t('admin.reservations.detail.placeableAfterPayment')}
          </li>
        ) : null}
      </ul>
      <Link
        to="../placement"
        relative="path"
        className="mt-2 inline-block text-sm text-champagne-300 underline"
      >
        {t('admin.reservations.detail.openPlacement')}
      </Link>

      {can('tables.reservation.update') && !cancelled ? (
        editing === 'composition' ? (
          <CompositionEditor table={table} orderId={orderId} onDone={() => setEditing(null)} />
        ) : editing === 'type' ? (
          <TableTypeEditor table={table} orderId={orderId} onDone={() => setEditing(null)} />
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing('composition')}>
              {t('admin.reservations.detail.edit')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing('type')}>
              {t('admin.reservations.detail.changeType')}
            </Button>
          </div>
        )
      ) : null}
    </Block>
  )
}

function CompositionEditor({
  table,
  orderId,
  onDone,
}: {
  table: ReservationTableDetail
  orderId: string
  onDone: () => void
}) {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()
  const options = useAdminQuery<DrinkOption[]>(
    ['admin-reservation-drinks', eventId],
    `/admin/events/${eventId}/reservations/drink-options`,
    eventId,
  )
  const [partySize, setPartySize] = useState(String(table.party_size))
  const [name, setName] = useState(table.name ?? '')
  // Chaque ligne garde son libellé : une boisson retirée de la carte depuis
  // reste lisible (et conservée) dans la composition de cette table.
  const [lines, setLines] = useState(
    table.items.map((item) => ({
      drink_item_id: item.drink_item_id,
      label: item.label,
      quantity: String(item.quantity),
    })),
  )
  const [search, setSearch] = useState('')
  const [reason, setReason] = useState('')
  const save = useAdminMutation<
    ReservationDetail,
    {
      party_size: number
      name: string | null
      items: { drink_item_id: string; quantity: number }[]
      reason: string
    }
  >(
    'put',
    () => `/admin/events/${eventId}/reservations/tables/${table.reservation_id}/composition`,
    eventId,
    [
      ['admin-reservation', eventId, orderId],
      ['admin-reservations', eventId],
      ['admin-orders', eventId],
    ],
  )

  const size = Number.parseInt(partySize, 10)
  const min = table.capacity_min ?? 1
  const max = table.capacity_max ?? 200
  const sizeValid = Number.isFinite(size) && size >= min && size <= max
  const parsed = lines.map((line) => ({
    drink_item_id: line.drink_item_id,
    quantity: Number.parseInt(line.quantity, 10),
  }))
  const linesValid = parsed.every(
    (line) => Number.isFinite(line.quantity) && line.quantity >= 1 && line.quantity <= 100,
  )
  const valid = sizeValid && linesValid && reason.trim().length >= 3

  // Toute la carte active, par catégorie ; la recherche porte sur le nom et
  // la catégorie (« champagne », « soft »).
  const taken = new Set(lines.map((line) => line.drink_item_id))
  const needle = search.trim().toLocaleLowerCase(i18n.language)
  const matches = (options.data ?? []).filter(
    (option) =>
      !needle ||
      option.label.toLocaleLowerCase(i18n.language).includes(needle) ||
      (option.category_label ?? '').toLocaleLowerCase(i18n.language).includes(needle),
  )
  const groups: { label: string; items: DrinkOption[] }[] = []
  for (const option of matches) {
    const label = option.category_label ?? t('admin.reservations.detail.otherDrinks')
    const group = groups.find((entry) => entry.label === label)
    if (group) group.items.push(option)
    else groups.push({ label, items: [option] })
  }

  return (
    <div className="mt-4 space-y-3 rounded-md border border-line-subtle p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-caption text-content-muted">
          {t('admin.reservations.detail.partySize', { min, max })}
          <input
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            value={partySize}
            onChange={(event) => setPartySize(event.target.value)}
            className={cn(INPUT, 'tabular-nums', !sizeValid && 'border-state-danger')}
          />
        </label>
        <label className="text-caption text-content-muted">
          {t('admin.tableRequests.groupName')}
          <input
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            className={INPUT}
          />
        </label>
      </div>

      <div>
        <p className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.reservations.detail.bottles')}
        </p>
        {lines.length === 0 ? (
          <p className="mt-1 text-sm text-content-muted">
            {t('admin.reservations.detail.noBottles')}
          </p>
        ) : (
          <ul className="mt-1 space-y-2">
            {lines.map((line, index) => (
              <li key={line.drink_item_id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 break-words text-sm text-content-primary">
                  {line.label}
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  aria-label={t('admin.reservations.detail.quantityOf', { drink: line.label })}
                  value={line.quantity}
                  onChange={(event) =>
                    setLines((current) =>
                      current.map((entry, position) =>
                        position === index ? { ...entry, quantity: event.target.value } : entry,
                      ),
                    )
                  }
                  className={cn(INPUT, 'mt-0 w-20 shrink-0 tabular-nums')}
                />
                <button
                  type="button"
                  aria-label={t('admin.reservations.detail.removeDrinkNamed', {
                    drink: line.label,
                  })}
                  onClick={() =>
                    setLines((current) => current.filter((_, position) => position !== index))
                  }
                  className="h-10 w-10 shrink-0 rounded-md border border-line text-content-muted"
                >
                  −
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <label className="block text-caption text-content-muted">
          {t('admin.reservations.detail.addDrink')}
          <input
            type="search"
            value={search}
            placeholder={t('admin.reservations.detail.searchDrink')}
            onChange={(event) => setSearch(event.target.value)}
            className={INPUT}
          />
        </label>
        <div className="mt-2 max-h-64 space-y-2 overflow-y-auto pr-1">
          {groups.length === 0 ? (
            <p className="text-sm text-content-muted">
              {options.isPending
                ? t('admin.reservations.loading')
                : t('admin.reservations.detail.noDrinkFound')}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.label}>
                <p className="text-caption text-content-muted">{group.label}</p>
                <ul className="mt-1 flex flex-wrap gap-1.5">
                  {group.items.map((option) => (
                    <li key={option.id}>
                      <button
                        type="button"
                        disabled={taken.has(option.id)}
                        onClick={() =>
                          setLines((current) => [
                            ...current,
                            { drink_item_id: option.id, label: option.label, quantity: '1' },
                          ])
                        }
                        className="min-h-9 rounded-md border border-line px-2.5 py-1 text-left text-sm text-content-primary disabled:opacity-40"
                      >
                        + {option.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>

      <label className="block text-caption text-content-muted">
        {t('admin.reservations.detail.reason')}
        <input
          value={reason}
          maxLength={500}
          placeholder={t('admin.reservations.detail.reasonPlaceholder')}
          onChange={(event) => setReason(event.target.value)}
          className={INPUT}
        />
      </label>
      <p className="text-caption text-content-muted">
        {t('admin.reservations.detail.priceUnchanged')}
      </p>

      {save.error instanceof ApiError ? (
        <p className="text-sm text-state-danger">{save.error.message}</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={!valid || save.isPending}
          onClick={() =>
            save.mutate(
              {
                party_size: size,
                name: name.trim() || null,
                items: parsed,
                reason: reason.trim(),
              },
              { onSuccess: onDone },
            )
          }
        >
          {t('admin.reservations.detail.save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t('admin.project.cancel')}
        </Button>
      </div>
    </div>
  )
}

/**
 * Ranger CETTE réservation sous un autre type (« Sur mesure », par exemple).
 * La composition suit le nouveau type si personne ne l'a ajustée ; sinon elle
 * est gardée — l'écran le dit avant de valider. Le prix ne bouge pas.
 */
function TableTypeEditor({
  table,
  orderId,
  onDone,
}: {
  table: ReservationTableDetail
  orderId: string
  onDone: () => void
}) {
  const { t } = useTranslation()
  const { eventId } = useAdminContext()
  const types = useAdminQuery<TableTypeChoice[]>(
    ['admin-reservation-table-types', eventId],
    `/admin/events/${eventId}/reservations/table-types`,
    eventId,
  )
  const [typeId, setTypeId] = useState(table.table_type_id)
  const [reason, setReason] = useState('')
  const save = useAdminMutation<ReservationDetail, { table_type_id: string; reason: string }>(
    'put',
    () => `/admin/events/${eventId}/reservations/tables/${table.reservation_id}/table-type`,
    eventId,
    [
      ['admin-reservation', eventId, orderId],
      ['admin-reservations', eventId],
      ['admin-orders', eventId],
    ],
  )
  const options = types.data ?? []
  const chosen = options.find((option) => option.id === typeId) ?? null
  const fits =
    chosen !== null &&
    table.party_size >= chosen.capacity_min &&
    table.party_size <= chosen.capacity_max
  const valid =
    chosen !== null && typeId !== table.table_type_id && fits && reason.trim().length >= 3

  return (
    <div className="mt-4 space-y-3 rounded-md border border-line-subtle p-3">
      <label className="block text-caption text-content-muted">
        {t('admin.tableRequests.tableType')}
        <select value={typeId} onChange={(event) => setTypeId(event.target.value)} className={INPUT}>
          {options.some((option) => option.id === table.table_type_id) ? null : (
            <option value={table.table_type_id}>{table.table_type_label}</option>
          )}
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label} ({option.capacity_min}–{option.capacity_max})
            </option>
          ))}
        </select>
      </label>
      {chosen && !fits ? (
        <p className="text-sm text-state-warning">
          {t('admin.tableRequests.typeCapacity', {
            min: chosen.capacity_min,
            max: chosen.capacity_max,
            count: table.party_size,
          })}
        </p>
      ) : null}
      <p className="text-caption text-content-muted">
        {table.composition_changed_at
          ? t('admin.reservations.detail.typeKeepsComposition')
          : t('admin.reservations.detail.typeReplacesComposition')}
      </p>
      <label className="block text-caption text-content-muted">
        {t('admin.reservations.detail.reason')}
        <input
          value={reason}
          maxLength={500}
          placeholder={t('admin.reservations.detail.reasonPlaceholder')}
          onChange={(event) => setReason(event.target.value)}
          className={INPUT}
        />
      </label>
      <p className="text-caption text-content-muted">
        {t('admin.reservations.detail.priceUnchanged')}
      </p>
      {save.error instanceof ApiError ? (
        <p className="text-sm text-state-danger">{save.error.message}</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={!valid || save.isPending}
          onClick={() =>
            save.mutate({ table_type_id: typeId, reason: reason.trim() }, { onSuccess: onDone })
          }
        >
          {t('admin.reservations.detail.saveType')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t('admin.project.cancel')}
        </Button>
      </div>
    </div>
  )
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-caption uppercase tracking-wide text-content-muted">{title}</p>
      {children}
    </div>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-content-muted">{label}</dt>
      <dd className={cn('tabular-nums', strong ? 'text-content-primary' : 'text-content-secondary')}>
        {value}
      </dd>
    </div>
  )
}

const INPUT =
  'mt-1 h-10 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary'
